import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { Event } from '../types/index.ts';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { MessageCircle, Search, Users, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export default function Chat() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadEventChats();
  }, []);

  const loadEventChats = async () => {
    try {
      setIsLoading(true);
      // Get user's tickets to show event chats
      const tickets = await api.getMyTickets();
      const eventIds = [...new Set(tickets.map(t => t.event_id))];
      
      // Get event details for each
      const eventPromises = eventIds.map(id => api.getEvent(id));
      const eventData = await Promise.all(eventPromises);
      setEvents(eventData);
    } catch (error) {
      toast.error('Failed to load chats');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.artist.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="space-y-4">
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
          Event Chats
        </h1>
        <p className="text-lg text-gray-400">
          Connect with other attendees
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <Input
          placeholder="Search event chats..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 glass border-white/10 h-12"
        />
      </div>

      {/* Event Chat List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="glass border-white/10 h-24 animate-pulse" />
          ))}
        </div>
      ) : filteredEvents.length > 0 ? (
        <div className="space-y-4">
          {filteredEvents.map((event) => (
            <EventChatCard
              key={event.event_id}
              event={event}
              onClick={() => navigate(`/chat/${event.event_id}`)}
            />
          ))}
        </div>
      ) : (
        <Card className="glass border-white/10 p-12 text-center">
          <MessageCircle className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg mb-4">
            {searchQuery ? 'No chats found' : 'No event chats yet'}
          </p>
          <p className="text-gray-500">
            {searchQuery ? 'Try a different search term' : 'Purchase tickets to join event chats'}
          </p>
        </Card>
      )}
    </div>
  );
}

function EventChatCard({ event, onClick }: { event: Event; onClick: () => void }) {
  return (
    <Card
      className="glass border-white/10 hover:border-white/20 transition-all cursor-pointer group"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center space-x-4">
          {/* Event Image */}
          <div className="h-16 w-16 rounded-lg overflow-hidden bg-background-subtle flex-shrink-0">
            {event.poster_url || event.image ? (
              <img
                src={event.poster_url || event.image}
                alt={event.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Calendar className="h-8 w-8 text-gray-600" />
              </div>
            )}
          </div>

          {/* Event Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="font-heading font-bold text-lg truncate">{event.title}</h3>
              {event.category && (
                <Badge variant="secondary" className="flex-shrink-0">
                  {event.category}
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-400 truncate">{event.artist}</p>
            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <Calendar className="h-3 w-3" />
                <span>{format(new Date(event.date), 'MMM dd')}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Users className="h-3 w-3" />
                <span>Chat Active</span>
              </div>
            </div>
          </div>

          {/* Arrow Icon */}
          <MessageCircle className="h-5 w-5 text-gray-400 group-hover:text-primary transition-colors flex-shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
}
